import { useRef, useState } from "react";
import { Card, CardBody, CardTitle, Table, Button, Input } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { GetManuals } from "../../actions/adminQueries";
import { ReplaceManual } from "../../actions/superUserMutations";
import { manualUrl } from "../../constants/ConstantVars";
import { formatDate, dateGetUnixTime } from "../../Utilities/dateUtils";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import PromptGeneric from "../Modal/PromptGeneric";
import Pacmanloader from "../Pacmanloader/Pacmanloader";

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const formatSize = (bytes) =>
  bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} kB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const getExtension = (fileName) => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex < 0 ? "" : fileName.substring(dotIndex).toLowerCase();
};

export default function AdminManuals() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const manuals = GetManuals();
  const fileInputs = useRef({});
  const [pendingReplace, setPendingReplace] = useState(null);

  const replaceManual = useMutation({ mutationFn: ReplaceManual });

  const handleFileSelected = (fileName, event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    if (getExtension(file.name) !== getExtension(fileName)) {
      toast.warn(intl.formatMessage({ id: "ManualFileFormatMismatch" }));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.warn(intl.formatMessage({ id: "ManualFileTooLarge" }));
      return;
    }

    setPendingReplace({ fileName, file });
  };

  const handleReplace = () => {
    const replace = pendingReplace;
    setPendingReplace(null);
    replaceManual.mutate(replace, {
      onSuccess: () => {
        toast.success(intl.formatMessage({ id: "ManualReplaced" }));
        queryClient.invalidateQueries({ queryKey: ["FetchManuals"] });
      },
      onError: (err) => {
        toast.warn(formatServiceError(intl, err?.response?.data));
      },
    });
  };

  return (
    <Card>
      <CardBody>
        <CardTitle>
          <FormattedMessage id="Manuals" />
        </CardTitle>
        <p className="text-muted">
          <FormattedMessage id="ManualsNoVersioningWarning" />
        </p>
        {manuals.isPending ? (
          <Pacmanloader />
        ) : manuals.isError ? (
          <p className="text-danger">
            {formatServiceError(intl, manuals.error?.response?.data)}
          </p>
        ) : manuals.data.length === 0 ? (
          <p>
            <FormattedMessage id="ManualsNoneFound" />
          </p>
        ) : (
          <Table size="sm" responsive className="w-auto">
            <thead>
              <tr>
                <th className="pe-4">
                  <FormattedMessage id="Filename" />
                </th>
                <th className="pe-4">
                  <FormattedMessage id="ManualLastModified" />
                </th>
                <th className="pe-4 text-end">
                  <FormattedMessage id="ManualSize" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {manuals.data.map((manual) => (
                <tr key={manual.fileName} style={{ verticalAlign: "middle" }}>
                  <td className="pe-4">
                    <a
                      href={manualUrl(
                        `${encodeURIComponent(manual.fileName)}?v=${dateGetUnixTime(manual.lastModified)}`,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {manual.fileName}
                    </a>
                  </td>
                  <td className="pe-4">
                    {formatDate(manual.lastModified, "yyyy-MM-dd HH:mm")}
                  </td>
                  <td className="pe-4 text-end">{formatSize(manual.size)}</td>
                  <td className="text-end">
                    <Input
                      type="file"
                      accept={getExtension(manual.fileName)}
                      innerRef={(element) => {
                        fileInputs.current[manual.fileName] = element;
                      }}
                      onChange={(event) =>
                        handleFileSelected(manual.fileName, event)
                      }
                      hidden
                    />
                    <Button
                      color="primary"
                      outline
                      size="sm"
                      disabled={replaceManual.isPending}
                      onClick={() =>
                        fileInputs.current[manual.fileName].click()
                      }
                    >
                      <FormattedMessage id="ManualReplaceFile" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
      <PromptGeneric
        isOpen={pendingReplace !== null}
        toggle={() => setPendingReplace(null)}
        onProceed={handleReplace}
        title={<FormattedMessage id="AreYouSure" />}
        text={
          pendingReplace &&
          intl.formatMessage(
            { id: "ManualReplaceConfirm" },
            {
              fileName: pendingReplace.fileName,
              newFileName: pendingReplace.file.name,
            },
          )
        }
        btnText="ManualReplaceFile"
        danger={true}
      />
    </Card>
  );
}
